import type { ComputedRef, Ref } from 'vue'
import type { SceneManager } from '@/three/SceneManager'
import type { HistoryManager } from '@/three/HistoryManager'
import type { useSceneStore, useSelectionStore } from '@/stores/scene'
import type { useModelLibraryStore } from '@/stores/modelLibrary'
import type { SceneObject } from '@/types/scene'
import type { ArrayGridAxis } from '@/utils/arrayDuplicate'
import { MODEL_EXTENSIONS } from '@/three/ModelImporter'
import { makeSceneObject, getThreeObject } from '@/three/ObjectManager'
import { getDropWorldPosition, getClickWorldPosition } from '@/three/DragHandler'
import { cloneSceneObjectAt } from '@/utils/cloneSceneObject'
import { createImportedSceneObjectFromLibraryMeta } from '@/utils/createImportedSceneObject'
import { deepClone } from '@/utils/deepClone'
import { getObjectFootprintXZ } from '@/utils/spawnPosition'
import { resolvePlacement, wantsSurfaceSnap } from '@/utils/surfaceSnap'
import type { DragGhostPreview } from '@/three/DragGhostPreview'
import { exclusiveConfirm } from '@/utils/exclusiveConfirm'

type SceneStore = ReturnType<typeof useSceneStore>
type SelectionStore = ReturnType<typeof useSelectionStore>
type ModelLibraryStore = ReturnType<typeof useModelLibraryStore>

export interface SceneCanvasActionsDeps {
  getSm: () => SceneManager | undefined
  sceneStore: SceneStore
  selectionStore: SelectionStore
  modelLibraryStore: ModelLibraryStore
  histManager: HistoryManager
  interactionReadonly: ComputedRef<boolean>
  currentFloorId: Ref<string>
  selectedId: Ref<string | null>
  selectedIds: Ref<string[]>
  draggingComponentType: Ref<string | null>
  contextMenu: Ref<{ visible: boolean; x: number; y: number; objectId: string }>
  connectingFromId: Ref<string | null>
  showImportDialog: Ref<boolean>
  importInitialFiles: Ref<File[]>
  arrayDialogVisible: Ref<boolean>
  isModelDragOver: Ref<boolean>
  clipboard: Ref<ReturnType<typeof makeSceneObject>[]>
  lastMouseInCanvas: Ref<{ clientX: number; clientY: number } | null>
  dragGhostPreview: DragGhostPreview
  onMarkDirty: () => void
  doDelete: (id: string) => void
  doDuplicate: (id: string) => void
  deselect: () => void
}

export function createSceneCanvasActions(deps: SceneCanvasActionsDeps) {
  function isModelFileDrop(e: DragEvent): boolean {
    const items = e.dataTransfer?.items
    if (!items) return false
    for (const item of Array.from(items)) {
      if (item.kind === 'file') {
        const name = item.getAsFile()?.name?.toLowerCase() ?? ''
        if (MODEL_EXTENSIONS.some((ext) => name.endsWith(ext))) return true
      }
    }
    return false
  }

  function onDragOver(e: DragEvent) {
    e.preventDefault()
    if (e.dataTransfer) {
      const hasModelFile = isModelFileDrop(e)
      deps.isModelDragOver.value = hasModelFile
      e.dataTransfer.dropEffect = hasModelFile ? 'copy' : 'move'
    }
    const type = deps.draggingComponentType.value
    const sm = deps.getSm()
    if (type && !isModelFileDrop(e) && sm) {
      const floor = deps.sceneStore.activeFloors().find((f) => f.id === deps.currentFloorId.value)
      const yElevation = floor?.elevation ?? 0
      deps.dragGhostPreview.update(type, e, yElevation, deps.sceneStore.activeObjects())
    }
  }

  function onDragLeave(e: DragEvent, container: HTMLElement | null) {
    if (!container) return
    const rect = container.getBoundingClientRect()
    const stillInside =
      e.clientX >= rect.left &&
      e.clientX <= rect.right &&
      e.clientY >= rect.top &&
      e.clientY <= rect.bottom
    if (!stillInside) {
      deps.isModelDragOver.value = false
      deps.dragGhostPreview.dispose()
    }
  }

  function onDrop(e: DragEvent) {
    if (deps.interactionReadonly.value) return
    deps.isModelDragOver.value = false
    deps.dragGhostPreview.dispose()
    deps.selectionStore.setDraggingComponentType(null)

    const files = Array.from(e.dataTransfer?.files ?? [])
    const modelFiles = files.filter((f) => MODEL_EXTENSIONS.some((ext) => f.name.toLowerCase().endsWith(ext)))
    if (modelFiles.length > 0) {
      deps.importInitialFiles.value = modelFiles
      deps.showImportDialog.value = true
      return
    }

    const sm = deps.getSm()
    const libraryModelId = e.dataTransfer?.getData('libraryModelId')?.trim()
    if (libraryModelId) {
      const meta = deps.modelLibraryStore.getModel(libraryModelId)
      if (!meta || !sm) return
      deps.histManager.push(deps.sceneStore.getEditorSnapshot())
      const fallbackX = Math.round(sm.controls.target.x)
      const fallbackZ = Math.round(sm.controls.target.z)
      const floor = deps.sceneStore.activeFloors().find((f) => f.id === deps.currentFloorId.value)
      const floorY = floor?.elevation ?? 0
      const pos = getDropWorldPosition(e, sm.renderer.domElement, sm.camera, { groundY: floorY })
      const placed = resolvePlacement(e, sm.renderer.domElement, sm.camera, {
        floorY,
        scene: sm.scene,
        objects: deps.sceneStore.activeObjects(),
        getThreeObject,
        snapToSurfaces: wantsSurfaceSnap(e),
      })
      const position: [number, number, number] = placed
        ? [placed.x, placed.y, placed.z]
        : pos
          ? [pos.x, floorY, pos.z]
          : [fallbackX, floorY, fallbackZ]
      const obj = createImportedSceneObjectFromLibraryMeta(meta, position)
      obj.floorId = deps.currentFloorId.value
      deps.sceneStore.addObject(obj)
      deps.onMarkDirty()
      return
    }

    const type = e.dataTransfer?.getData('componentType')
    if (!type || !sm) return
    const fallbackX = Math.round(sm.controls.target.x)
    const fallbackZ = Math.round(sm.controls.target.z)
    const floor = deps.sceneStore.activeFloors().find((f) => f.id === deps.currentFloorId.value)
    const floorY = floor?.elevation ?? 0
    const pos = getDropWorldPosition(e, sm.renderer.domElement, sm.camera, { groundY: floorY })
    const placed = resolvePlacement(e, sm.renderer.domElement, sm.camera, {
      floorY,
      scene: sm.scene,
      objects: deps.sceneStore.activeObjects(),
      getThreeObject,
      snapToSurfaces: wantsSurfaceSnap(e),
      type, // 铺地件按脚下建筑地面吸附，避免共面
    })
    const position: [number, number, number] = placed
      ? [placed.x, placed.y, placed.z]
      : pos
        ? [pos.x, floorY, pos.z]
        : [fallbackX, floorY, fallbackZ]
    deps.histManager.push(deps.sceneStore.getEditorSnapshot())
    const obj = makeSceneObject(type, position)
    obj.floorId = deps.currentFloorId.value
    deps.sceneStore.addObject(obj)
    deps.onMarkDirty()
  }

  function closeContextMenu() {
    deps.contextMenu.value.visible = false
  }

  function startConnect(connectionManager: { startConnecting: (id: string) => void } | undefined) {
    deps.contextMenu.value.visible = false
    const fromId = deps.selectedId.value
    if (!fromId) return
    deps.connectingFromId.value = fromId
    connectionManager?.startConnecting(fromId)
  }

  async function deleteSelected() {
    deps.contextMenu.value.visible = false
    const n = deps.selectedIds.value.length
    if (n > 1) {
      const ok = await exclusiveConfirm(`确定删除选中的 ${n} 个对象？`, '删除确认')
      if (!ok) return
      deps.histManager.push(deps.sceneStore.getEditorSnapshot())
      const ids = [...deps.selectedIds.value]
      deps.deselect()
      ids.forEach((sid) => deps.sceneStore.removeObject(sid))
    } else if (deps.selectedId.value) {
      deps.doDelete(deps.selectedId.value)
    }
  }

  function ctxCopy() {
    deps.contextMenu.value.visible = false
    const id = deps.selectedId.value
    if (!id) return
    const ids = deps.selectedIds.value.length > 1 ? deps.selectedIds.value : [id]
    deps.clipboard.value = ids
      .map((sid) => deps.sceneStore.activeObjects().find((o) => o.id === sid))
      .filter((o): o is NonNullable<typeof o> => !!o)
      .map((o) => deepClone(o))
  }

  function ctxPaste() {
    deps.contextMenu.value.visible = false
    if (!deps.clipboard.value.length) return
    deps.histManager.push(deps.sceneStore.getEditorSnapshot())
    const first = deps.clipboard.value[0]
    const sm = deps.getSm()
    const canvas = sm?.renderer?.domElement
    let basePos: [number, number, number]
    if (deps.lastMouseInCanvas.value && canvas && sm) {
      const floor = deps.sceneStore.activeFloors().find((f) => f.id === deps.currentFloorId.value)
      const yElevation = floor?.elevation ?? 0
      const pos = getClickWorldPosition(deps.lastMouseInCanvas.value, canvas, sm.camera, { groundY: yElevation })
      basePos = pos ? [pos.x, yElevation, pos.z] : [first.position[0] + 2, first.position[1], first.position[2] + 2]
    } else {
      basePos = [first.position[0] + 2, first.position[1], first.position[2] + 2]
    }
    const dx = basePos[0] - first.position[0]
    const dz = basePos[2] - first.position[2]
    const floors = deps.sceneStore.activeFloors()
    const tgtFloorId = deps.currentFloorId.value
    const tgtElev = floors.find((f) => f.id === tgtFloorId)?.elevation ?? 0
    const newObjs = deps.clipboard.value.map((src) => {
      const srcElev = floors.find((f) => f.id === (src.floorId ?? 'f1'))?.elevation ?? 0
      const newY = src.position[1] + (tgtElev - srcElev)
      const obj = cloneSceneObjectAt(src, [src.position[0] + dx, newY, src.position[2] + dz])
      obj.floorId = tgtFloorId
      return obj
    })
    newObjs.forEach((o) => deps.sceneStore.addObject(o))
    deps.selectionStore.select(newObjs[0].id, { x: 0, y: 0 })
    newObjs.slice(1).forEach((o) => deps.selectionStore.multiSelect(o.id, { x: 0, y: 0 }))
  }

  function ctxDuplicate() {
    deps.contextMenu.value.visible = false
    if (deps.selectedId.value) deps.doDuplicate(deps.selectedId.value)
  }

  function openArrayFromContext() {
    deps.contextMenu.value.visible = false
    if (!deps.selectedId.value && deps.selectedIds.value.length === 0) return
    deps.arrayDialogVisible.value = true
  }

  function ctxRename() {
    const id = deps.contextMenu.value.objectId
    deps.contextMenu.value.visible = false
    if (!id) return
    deps.selectionStore.requestRename(id)
  }

  function onArrayDuplicateConfirm(cfg: {
    rows: number
    cols: number
    rowSpacing: number
    colSpacing: number
    axis: ArrayGridAxis
  }) {
    const ids =
      deps.selectedIds.value.length > 1 ? [...deps.selectedIds.value] : deps.selectedId.value ? [deps.selectedId.value] : []
    const sources = ids
      .map((id) => deps.sceneStore.activeObjects().find((o) => o.id === id))
      .filter((o): o is SceneObject => !!o)
    if (!sources.length) return
    deps.histManager.push(deps.sceneStore.getEditorSnapshot())
    const colGap = Math.max(0, cfg.colSpacing)
    const rowGap = Math.max(0, cfg.rowSpacing)
    for (const src of sources) {
      const { w, d } = getObjectFootprintXZ(src)
      let colStep: number
      let rowStep: number
      if (cfg.axis === 'x') {
        colStep = w + colGap
        rowStep = d + rowGap
      } else {
        colStep = d + colGap
        rowStep = w + rowGap
      }
      const [bx, by, bz] = src.position
      for (let r = 0; r < cfg.rows; r++) {
        for (let c = 0; c < cfg.cols; c++) {
          if (r === 0 && c === 0) continue
          const dx = cfg.axis === 'x' ? c * colStep : r * rowStep
          const dz = cfg.axis === 'x' ? r * rowStep : c * colStep
          const pos: [number, number, number] = [bx + dx, by, bz + dz]
          const name = `${src.name}-r${r + 1}-c${c + 1}`
          deps.sceneStore.addObject(cloneSceneObjectAt(src, pos, name))
        }
      }
    }
    deps.onMarkDirty()
  }

  function ctxSelectAll() {
    deps.contextMenu.value.visible = false
    const objs = deps.sceneStore.activeObjects().filter((o) => !o.hidden && (o.floorId ?? 'f1') === deps.currentFloorId.value)
    if (!objs.length) return
    deps.selectionStore.select(objs[0].id, { x: 0, y: 0 })
    objs.slice(1).forEach((o) => deps.selectionStore.multiSelect(o.id, { x: 0, y: 0 }))
  }

  function onToggleHidden(id: string) {
    const obj = deps.sceneStore.activeObjects().find((o) => o.id === id)
    if (!obj) return
    deps.histManager.push(deps.sceneStore.getEditorSnapshot())
    deps.sceneStore.updateObject(id, { hidden: !obj.hidden })
  }

  return {
    onDragOver,
    onDragLeave,
    onDrop,
    closeContextMenu,
    startConnect,
    deleteSelected,
    ctxCopy,
    ctxPaste,
    ctxDuplicate,
    openArrayFromContext,
    ctxRename,
    onArrayDuplicateConfirm,
    ctxSelectAll,
    onToggleHidden,
  }
}
